/*
 * Jira Ticket:
 * Created Date: Mon, 23rd Mar 2020, 16:45:28 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState } from 'react';
import {
  Confirm,
  Datagrid,
  FunctionField,
  ReferenceManyField,
  TextField,
  useDataProvider,
  useRefresh,
  useTranslate,
  useNotify,
  useRedirect,
  
} from 'react-admin';
import { ThumbUp, ThumbDown, Delete } from '@material-ui/icons';
import { ActionButton } from 'Components/Buttons';
import { tabbedLayoutDatagrid, actionButtonStyles } from 'styles';
import { PostPagination } from '../../../../Components/CustomPagination/pagination';
import { isManager, isProjectManager, isSelf } from 'utils/roles';

function canEdit(permissions, record) {
  const organisationId = record.organisation?.id;
  if (organisationId === null) {
    return false;
  }

  switch (record.type) {
    case 'FREELANCER':
      return isProjectManager(permissions, organisationId);
    case 'EMPLOYEE':
      return isManager(permissions, organisationId);
    default:
      return false;
  }
}

function canAccept(userId, permissions, record) {
  return isSelf(userId) || canEdit(permissions, record);
}

function canReject(userId, permissions, record) {
  return isSelf(userId) || canEdit(permissions, record);
}

function canRevoke(userId, permissions, record) {
  return canEdit(permissions, record);
}

const ProjectsField = ({ record }) => {
  const array = record.projects.map(item => `${item.name}`);
  return array.join('; ');
};

const InvitesTab = ({
  className,
  hasList,
  hasShow,
  hasEdit,
  dispatch,
  formValues,
  ...props
}) => {
  const classes = tabbedLayoutDatagrid();
  const buttonClasses = actionButtonStyles();
  const refresh = useRefresh();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [dataToSubmit, setDataToSubmit] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleActionButton = ({ action, record }) => {
    setDataToSubmit({
      action,
      id: record.id,
      invitingOrg: record.organisation.name,
    });
    return setShowConfirmModal(true);
  };

  const handleUpdateInvite = ({ action, id }) => {
    if (action) {
      return dataProvider(action, 'Employment', { id })
        .then(result => {
          if (result.data.success) {
            if (action === 'REVOKE') {
              redirect(props['basePath']);
              return notify('notification.employment.revokeSuccess', 'info');
            }
            refresh();
            if (action === 'ACCEPT') {
              redirect(`${props.match.url}/1`);
            }
            return notify('notification.contract.updateSuccess', 'info');
          }
          refresh();
          return notify('notification.contract.updateFailure', 'warning');
        })
        .catch(error => {
          setShowConfirmModal(false);
          const {
            extensions: { code },
            message,
          } = error.graphQLErrors[0];
          if (code !== 'INTERNAL_SERVER_ERROR') {
            return notify(message, 'warning');
          }
          return notify('error.generic', 'warning');
        });
    }
  };

//  console.log ("Record", props.record)

 const hasAccount = props.record.hasAccount;
    
//  console.log ("Has Account", hasAccount)

  return (
    <div className={classes.root}>
      <ReferenceManyField
        reference="Employment"
        target={!hasAccount ? 'email' : 'user'}
        filter={{
          statuses: ['PENDING', 'REJECTED'],
          email:!hasAccount? props.record.email : undefined,
        }}
        // email={props.record.email}
        perPage={25}
        pagination={<PostPagination />}
        bulkActionButtons={false}
        {...props}
      >
        <Datagrid>
          <TextField
            source="organisation.name"
            label={'resources.Employment.fields.invitingOrg'}
            sortable={false}
          />
          <FunctionField
            label={'resources.User.fields.projects'}
            render={(record) =>
              record.projects ? <ProjectsField record={record} /> : 'All'
            }
          />
          <FunctionField
            render={(record) =>
              record.status === 'PENDING' &&
              canAccept(props.id, props.permissions, record) && (
                <ActionButton
                  icon={<ThumbUp />}
                  name={'actions.accept'}
                  onClick={() =>
                    handleActionButton({ action: 'ACCEPT', record })
                  }
                  className={buttonClasses.approve}
                />
              )
            }
          />
          <FunctionField
            render={(record) =>
              record.status === 'PENDING' &&
              canReject(props.id, props.permissions, record) && (
                <ActionButton
                  icon={<ThumbDown />}
                  name={'actions.reject'}
                  onClick={() =>
                    handleActionButton({ action: 'REJECT', record })
                  }
                  className={buttonClasses.decline}
                />
              )
            }
          />
          <FunctionField
            render={(record) =>
              canRevoke(props.id, props.permissions, record) && (
                <ActionButton
                  icon={<Delete />}
                  name={'actions.revoke'}
                  onClick={() =>
                    handleActionButton({ action: 'REVOKE', record })
                  }
                  className={buttonClasses.decline}
                />
              )
            }
          />
        </Datagrid>
      </ReferenceManyField>
      <Confirm
        isOpen={showConfirmModal}
        title={translate('resources.Employment.fields.updateInviteModalTitle', {
          action:
            dataToSubmit.action &&
            dataToSubmit.action.charAt(0).toUpperCase() +
              dataToSubmit.action.substr(1).toLowerCase(),
        })}
        content={translate(
          'resources.Employment.fields.updateInviteModalContent',
          {
            invitingOrgName: dataToSubmit && dataToSubmit.invitingOrg,
            action: dataToSubmit.action && dataToSubmit.action.toLowerCase(),
          }
        )}
        onConfirm={() => handleUpdateInvite(dataToSubmit)}
        onClose={() => setShowConfirmModal(false)}
      />
    </div>
  );
};

export default InvitesTab;
